import React from 'react';
import { StarIcon } from 'lucide-react';
import { buyerReviews, type BuyerReview } from '../data/buyerReviews';
import { ReviewAvatar } from './ReviewAvatar';

/**
 * Buyer reviews. Renders nothing while data/buyerReviews.ts is empty.
 */
export function BuyerReviews() {
  if (buyerReviews.length === 0) return null;
  const [featured, ...rest] = buyerReviews;
  return <section aria-labelledby="buyer-reviews-heading" className="w-full bg-white text-ink" data-id="element-50" data-mp="2hgtuy1cnq569">
      <div className="max-w-6xl mx-auto px-5 py-16 sm:py-20" data-id="element-51" data-mp="2ci0fw4hq263z">
        <div className="text-center" data-id="element-52" data-mp="2cgfmii758t7p">
          <h2 id="buyer-reviews-heading" className="font-display text-3xl font-bold text-rink sm:text-5xl" data-id="element-53" data-mp="28fvn17z3ldie">
            Coaches using it this season
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-ink-soft" data-id="element-54" data-mp="6oijoncs3dog">
            What coaches running mixed-level group classes say after putting
            the 3-Lane Lesson on the ice.
          </p>
        </div>

        <figure className="mt-10 grid items-center gap-6 rounded-3xl bg-glacier-soft p-6 sm:p-10 md:grid-cols-[180px_1fr] md:gap-10" data-id="element-55" data-mp="275pihkacae9d">
          <ReviewAvatar review={featured} index={0} className="mx-auto h-28 w-28 ring-4 ring-white shadow-card md:h-40 md:w-40" textClassName="text-3xl md:text-4xl" data-id="element-56" data-mp="2c6d944unot32" />
          <div data-id="element-57" data-mp="2368b2lbcr71k">
            {featured.rating && <Stars rating={featured.rating} data-id="element-58" data-mp="2qtpyy5rhxkz9" />}
            <blockquote className="mt-1 font-display text-lg font-semibold leading-snug text-ink sm:text-2xl" data-id="element-59" data-mp="2qw2ob3zl2xmc">
              “{featured.quote}”
            </blockquote>
            <figcaption className="mt-5" data-id="element-60" data-mp="2h3desenlfttn">
              <ReviewAuthor review={featured} data-id="element-61" data-mp="2h2c1e3j3uv8r" />
            </figcaption>
          </div>
        </figure>

        {rest.length > 0 && <ul className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-62" data-mp="39skbr97egbcs">
            {rest.map((review, i) => <li key={`${review.name}-${review.location}`} className="flex flex-col rounded-card border border-ice-dark bg-white p-6 shadow-card" data-id="element-63" data-mp="3ekltvbr90waq" data-mpi={i}>
                {review.rating && <Stars rating={review.rating} data-id="element-64" data-mp="3jgzcyrkmr7tc" />}
                <blockquote className="mt-1 flex-1 text-[15px] leading-relaxed text-ink" data-id="element-65" data-mp="3eh3bayzensgy">
                  “{review.quote}”
                </blockquote>
                <div className="mt-5 flex items-center gap-3 border-t border-ice-dark pt-4" data-id="element-66" data-mp="39hqrkn3tz1il">
                  <ReviewAvatar review={review} index={i + 1} data-id="element-67" data-mp="35i81ze12e29f" />
                  <ReviewAuthor review={review} data-id="element-68" data-mp="30pf2b4kgdk47" />
                </div>
              </li>)}
          </ul>}
      </div>
    </section>;
}
function ReviewAuthor({
  review
}: {
  review: BuyerReview;
}) {
  return <div className="text-sm" data-id="element-69" data-mp="2qt3na6thag62">
      <p className="font-semibold text-rink" data-id="element-70" data-mp="2qpxuaghqau52">{review.name}</p>
      <p className="text-ink-soft" data-id="element-71" data-mp="2lre8qh71rkd8">
        {review.role} · {review.location}
      </p>
    </div>;
}
function Stars({
  rating
}: {
  rating: number;
}) {
  const value = Math.max(0, Math.min(5, Math.round(rating)));
  return <div className="mb-2 flex gap-0.5" aria-label={`Rated ${value} out of 5`} data-id="element-72" data-mp="xb4lbkjatwb0">
      {Array.from({
      length: 5
    }, (_, i) => <StarIcon key={i} aria-hidden="true" className={`h-4 w-4 ${i < value ? 'fill-amber text-amber' : 'fill-ice-dark text-ice-dark'}`} data-id="element-73" data-mp="169q9bv7ji3j6" />)}
    </div>;
}